/* eslint-disable react/jsx-props-no-spreading */
import React, { useEffect, useState } from 'react';
import {
  Alert,
  AlertTitle,
  Autocomplete,
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { Traveler } from '@marageti/z4-sdk/lib/people';
import {
  GroupedTemplate,
  SMS_MAX_LENGTH,
  SMS_MESSAGE_PREFIX,
  getSmsLengthGuidance,
  isSmsMessageSendable,
  simplifyString,
  stripHtmlToPlainText,
} from './helpers';

type TwoWaySmsViewProps = {
  open: boolean;
  closeDialog: () => void;
  traveler: Traveler;
  formattedPhone: string;
  localTimeLabel: string;
  zicassoNumber: string;
  hasPartnerSmsNumber: boolean;
  templates: GroupedTemplate[];
  errorReason: string | null;
  onDismissError: () => void;
  onSubmit: (message: string) => Promise<void>;
};

const TwoWaySmsView = ({
  open,
  closeDialog,
  traveler,
  formattedPhone,
  localTimeLabel,
  zicassoNumber,
  hasPartnerSmsNumber,
  templates,
  errorReason,
  onDismissError,
  onSubmit,
}: TwoWaySmsViewProps) => {
  const [message, setMessage] = useState(SMS_MESSAGE_PREFIX);
  const [selectedTemplate, setSelectedTemplate] = useState<GroupedTemplate | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (!open) {
      setMessage(SMS_MESSAGE_PREFIX);
      setSelectedTemplate(null);
      setIsSubmitting(false);
    }
  }, [open]);

  const guidance = getSmsLengthGuidance(message);
  const canSend = isSmsMessageSendable(message) && hasPartnerSmsNumber;

  const onTemplateChange = (
    _e: React.SyntheticEvent<Element, Event>,
    template: GroupedTemplate | null,
  ) => {
    setSelectedTemplate(template);
    if (template?.body) {
      setMessage(`${SMS_MESSAGE_PREFIX}${stripHtmlToPlainText(template.body)}`);
    }
  };

  const onMessageChange = (value: string) => {
    if (!value.startsWith(SMS_MESSAGE_PREFIX)) {
      setMessage(SMS_MESSAGE_PREFIX);
      return;
    }
    setMessage(value);
  };

  const handleSubmit = async () => {
    if (!canSend) return;
    onDismissError();
    setIsSubmitting(true);
    try {
      await onSubmit(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const recipientLabel = `To ${traveler.fullName}${formattedPhone ? ` (${formattedPhone})` : ''}${localTimeLabel ? ` • ${localTimeLabel}` : ''}`;

  return (
    <Dialog open={open} onClose={closeDialog} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ px: 3, pb: 1 }}>
        Send SMS
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, fontWeight: 400 }}>
          Send a text when a traveler hasn&apos;t replied. Follow up with timing, not repetition.
        </Typography>
      </DialogTitle>
      <DialogContent sx={{ px: 3, pb: 0 }}>
        <Stack spacing={2.5} sx={{ pt: 1 }}>
          <Autocomplete
            options={templates}
            groupBy={(option) => option.group}
            getOptionLabel={(option) => option.title || ''}
            value={selectedTemplate}
            onChange={onTemplateChange}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Template"
                fullWidth
                sx={{ minWidth: { desktop: '300px' } }}
              />
            )}
            renderOption={({ key, ...props }, option, _, ownerState) => (
              <MenuItem key={key} {...props}>
                <Typography variant="body1">{ownerState.getOptionLabel(option)}</Typography>
              </MenuItem>
            )}
            filterOptions={(options, params) => {
              const searchText = simplifyString(params.inputValue);
              return options.filter((option) => simplifyString(option.title).includes(searchText));
            }}
          />

          <Box>
            <TextField
              label="Message"
              multiline
              minRows={5}
              fullWidth
              value={message}
              onChange={(e) => onMessageChange(e.target.value)}
            />
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
              {guidance.count}
              /
              {SMS_MAX_LENGTH}
              {' '}
              |
              {' '}
              {guidance.caption}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
              Sent from your Zicasso number
              {zicassoNumber ? ` ${zicassoNumber}` : ''}
              . Replies land in this conversation.
            </Typography>
          </Box>

          <Box
            sx={{
              px: 2,
              py: 1.5,
              borderRadius: 1,
              backgroundColor: 'info.light',
            }}
          >
            <Typography variant="body2" color="primary">
              {recipientLabel}
            </Typography>
          </Box>

          {errorReason && (
            <Alert severity="error">
              <AlertTitle>We couldn&apos;t send your SMS</AlertTitle>
              {errorReason}
            </Alert>
          )}
        </Stack>
      </DialogContent>

      <Stack
        direction="row"
        spacing={2}
        sx={(theme) => ({
          borderTop: `1px solid ${theme.palette.divider}`,
          py: 2.5,
          px: 3,
          mt: 2,
        })}
      >
        <LoadingButton
          variant="contained"
          onClick={handleSubmit}
          loading={isSubmitting}
          disabled={!canSend}
        >
          Send
        </LoadingButton>
        <Button variant="outlined" onClick={closeDialog}>
          Cancel
        </Button>
      </Stack>
    </Dialog>
  );
};

export default TwoWaySmsView;
